import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './DailyAttendancePage.css';
import { FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaCalendar, FaRedo, FaDownload, FaPrint } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const DailyAttendancePage = () => {
  const { currentUser } = useAuth();
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ present: 0, absent: 0, total: 0 });
  
  // Use the centralized API configuration
  const API_URL = API_CONFIG.BASE_URL;

  // Sync Firebase Auth users to Firestore users collection
  const syncAuthUsersToFirestore = useCallback(async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      // Sync users from Firebase Auth to Firestore
      const response = await axios.post(`${API_URL}/api/auth/sync-users`, {}, { headers });
      console.log('Synced users to Firestore:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to sync users to Firestore:', error);
      return null;
    }
  }, [currentUser, API_URL]);

  // Load all users from database (users collection)
  const loadAllUsersFromDatabase = useCallback(async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      // First, try to sync users from Firebase Auth to Firestore
      await syncAuthUsersToFirestore();
      
      // Then fetch users from Firestore users collection
      const response = await axios.get(`${API_URL}/api/users`, { headers });
      const allUsers = Array.isArray(response.data) ? response.data : [];
      console.log('Loaded all users from Firestore users collection:', allUsers.length, 'users');
      return allUsers;
    } catch (error) {
      console.error('Failed to load all users from database:', error);
      
      // Handle Firebase quota exceeded error
      if (error.message?.includes('quota-exceeded') || error.message?.includes('auth/quota-exceeded')) {
        console.warn('Firebase quota exceeded - trying to load from Firestore users collection');
        try {
          // Try to load from Firestore users collection (no quota issues)
          const idToken = await currentUser.getIdToken(true);
          const headers = { Authorization: `Bearer ${idToken}` };
          const usersResponse = await axios.get(`${API_URL}/api/users`, { headers });
          const users = Array.isArray(usersResponse.data) ? usersResponse.data : [];
          console.log('Loaded users from Firestore collection:', users.length, 'users');
          return users;
        } catch (firestoreError) {
          console.warn('Firestore users collection also failed - using local punching records only');
          return [];
        }
      }
      
      if (error.response?.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
        console.warn('Users endpoint not available - trying staff endpoint');
        // Fallback to staff endpoint
        try {
          const idToken = await currentUser.getIdToken(true);
          const headers = { Authorization: `Bearer ${idToken}` };
          const staffResponse = await axios.get(`${API_URL}/api/staff`, { headers });
          const staffUsers = Array.isArray(staffResponse.data) ? staffResponse.data : [];
          console.log('Fallback: Loaded staff users:', staffUsers.length, 'users');
          return staffUsers;
        } catch (staffError) {
          console.error('Staff endpoint also failed:', staffError);
          return [];
        }
      }
      return [];
    }
  }, [currentUser, API_URL, syncAuthUsersToFirestore]);

  // Load all users from Firebase Auth (via server API)
  const loadAllFirebaseUsers = useCallback(async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      // Try to fetch all users from Firebase Auth via server API
      const response = await axios.get(`${API_URL}/api/auth/users/all`, { headers });
      const allUsers = Array.isArray(response.data) ? response.data : [];
      return allUsers;
    } catch (error) {
      console.error('Failed to load Firebase Auth users:', error);
      if (error.response?.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
        console.warn('Firebase Auth users endpoint not available');
        return [];
      }
      return [];
    }
  }, [currentUser, API_URL]);

  // Load punching time records from local storage
  const loadPunchingTimes = () => {
    try {
      const stored = localStorage.getItem('punchingTimes');
      const punchingTimes = stored ? JSON.parse(stored) : [];
      return punchingTimes;
    } catch (error) {
      console.error('Failed to load punching times:', error);
      return [];
    }
  };

  // Fetch all staff data from multiple sources
  const fetchStaffData = useCallback(async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      let allStaffData = [];
      
      // 1. Try to fetch from API first
      try {
        const staffRes = await axios.get(`${API_URL}/api/staff`, { headers });
        const apiStaff = Array.isArray(staffRes.data) ? staffRes.data : [];
        allStaffData = [...allStaffData, ...apiStaff];
      } catch (staffErr) {
        console.error('Failed to fetch staff from API:', staffErr);
        if (staffErr.response?.status === 404 || staffErr.message?.includes('404') || staffErr.message?.includes('Not Found')) {
          console.warn('Staff endpoint not available - loading from Firebase');
        } else {
          setError(`Failed to load staff: ${staffErr.response?.data?.error || staffErr.message}`);
        }
      }
      
      // 2. Load all users from database (users collection)
      const firebaseUsers = await loadAllUsersFromDatabase();
      
      // 3. Process users data to staff format
      const processedStaff = firebaseUsers.map(user => ({
        _id: user._id || user.id,
        name: user.name || user.displayName || 'Unknown',
        email: user.email || '',
        mobile: user.phoneNumber || user.mobile || '',
        role: user.role || 'staff',
        department: user.department || '',
        address: user.address || '',
        company: user.company || null,
        isActive: user.isActive !== false,
        createdAt: user.createdAt || user.created_at,
        lastLogin: user.lastLogin || user.last_login
      }));
      
      allStaffData = [...allStaffData, ...processedStaff];
      
      // 4. Load all users from Firebase Auth (fallback)
      const authUsers = await loadAllFirebaseUsers();
      
      // 5. Combine all data sources and remove duplicates
      const combinedData = [...allStaffData];
      
      // Add Firebase Auth users that are not already in the staff data
      authUsers.forEach(authUser => {
        const exists = combinedData.some(staff => 
          staff.email === authUser.email || 
          staff._id === authUser.uid ||
          staff.id === authUser.uid
        );
        
        if (!exists) {
          // Create staff record from Firebase Auth user
          const staffFromAuth = {
            _id: authUser.uid || `auth-${authUser.email}`,
            name: authUser.displayName || authUser.name || authUser.email.split('@')[0],
            email: authUser.email,
            role: authUser.role || authUser.customClaims?.role || 'staff',
            department: authUser.department || authUser.customClaims?.department || 'N/A',
            company: authUser.company || authUser.customClaims?.company || { name: 'N/A' },
            isFromAuth: true, // Flag to indicate this user is from Firebase Auth
            createdAt: authUser.createdAt || new Date().toISOString()
          };
          combinedData.push(staffFromAuth);
        }
      });
      
      // Remove duplicates based on email
      const uniqueStaff = combinedData.filter((staff, index, self) => 
        index === self.findIndex(s => s.email === staff.email)
      );
      
      
      return uniqueStaff;
    } catch (err) {
      console.error('Unexpected error in fetchStaffData:', err);
      setError(`Unexpected error: ${err.message}`);
      return [];
    }
  }, [currentUser, API_URL, loadAllFirebaseUsers, loadAllUsersFromDatabase]);

  // Process attendance data - compare users collection with punch-in records
  const processAttendanceData = useCallback((staffList, punchingTimes) => {
    const today = selectedDate;
    
    console.log('🔍 DailyAttendance Debug:', {
      selectedDate: today,
      punchingTimesCount: punchingTimes.length,
      punchingTimes: punchingTimes,
      staffListCount: staffList.length,
      staffList: staffList
    });
    
    // Get users who punched in today
    const todayPunchingRecords = punchingTimes.filter(record => record.date === today);
    const punchedInEmails = [...new Set(todayPunchingRecords.map(record => record.email))];
    
    console.log('🔍 Today\'s punching records:', {
      todayPunchingRecords: todayPunchingRecords,
      punchedInEmails: punchedInEmails
    });
    
    // If we have staff data from users collection, process all staff members
    if (staffList.length > 0) {
      const attendance = staffList.map(staff => {
        const email = staff.email;
        const hasPunchedIn = punchedInEmails.includes(email);
        
        // Get all punching records for today if user punched in
        let punchingRecords = [];
        let latestPunchingRecord = null;
        if (hasPunchedIn) {
          punchingRecords = todayPunchingRecords.filter(record => record.email === email);
          // Get the most recent record (latest punch in time)
          latestPunchingRecord = punchingRecords.sort((a, b) => 
            new Date(b.punchIn || b.createdAt) - new Date(a.punchIn || a.createdAt)
          )[0];
        }
        
        // Get login/logout times from the most recent record
        let loginTime = null;
        let logoutTime = null;
        if (hasPunchedIn && latestPunchingRecord) {
          loginTime = latestPunchingRecord.punchIn;
          logoutTime = latestPunchingRecord.punchOut;
        }
        
        return {
          ...staff,
          isPresent: hasPunchedIn,
          loginTime,
          logoutTime,
          loginRecords: punchingRecords,
          latestPunchingRecord
        };
      });
      
      // Calculate stats
      const presentCount = attendance.filter(a => a.isPresent).length;
      const absentCount = attendance.filter(a => !a.isPresent).length;
      
      setStats({
        present: presentCount,
        absent: absentCount,
        total: attendance.length
      });
      
      return attendance;
    }
    
    // Fallback: If no staff data from users collection, show message
    setStats({ present: 0, absent: 0, total: 0 });
    return [];
  }, [selectedDate]);

  // Load attendance data from attendance API
  const loadAttendanceData = useCallback(async () => {
    try {
      setLoading(true);
      
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      // Try to fetch from attendance API first
      try {
        const response = await axios.get(`${API_URL}/api/attendance/daily?date=${selectedDate}`, { headers });
        const attendanceData = response.data;
        
        console.log('🔍 Attendance API response:', attendanceData);
        
        setAttendanceData(attendanceData.records || []);
        setStats({
          present: attendanceData.present || 0,
          absent: attendanceData.absent || 0,
          total: attendanceData.totalUsers || 0
        });
        
        setError('');
      } catch (apiError) {
        console.error('Attendance API failed, falling back to local processing:', apiError);
        
        // Fallback to local processing if API fails
        const staffData = await fetchStaffData();
        const punchingTimes = loadPunchingTimes();
        
        console.log('🔍 Fallback - Loaded data for DailyAttendance:', {
          staffDataCount: staffData.length,
          punchingTimesCount: punchingTimes.length,
          selectedDate: selectedDate
        });
        
        // Process attendance
        const attendance = processAttendanceData(staffData, punchingTimes);
        setAttendanceData(attendance);
      }
    } catch (err) {
      console.error('Failed to load attendance data:', err);
      setError(`Failed to load attendance data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentUser, API_URL, selectedDate, fetchStaffData, processAttendanceData]);

  // Load data on component mount and when date changes
  useEffect(() => {
    if (currentUser) {
      loadAttendanceData();
    }
  }, [currentUser, loadAttendanceData]);


  // Get status color
  const getStatusColor = (isPresent) => {
    return isPresent ? '#28a745' : '#dc3545';
  };

  // Get status icon
  const getStatusIcon = (isPresent) => {
    return isPresent ? <FaCheckCircle /> : <FaTimesCircle />;
  };

  // Helper function to format department display
  const formatDepartment = (department) => {
    if (!department) return 'N/A';
    // If it looks like an ID (contains letters and numbers, long string), show as is
    // If it's a short string or contains spaces, it's likely a name
    if (department.length > 20 && /^[a-zA-Z0-9]+$/.test(department)) {
      return department; // Likely an ID, show as is
    }
    return department; // Likely a name, show as is
  };

  // Handle date change
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Refresh data
  const handleRefresh = () => {
    loadAttendanceData();
  };

  // Update attendance record manually
  const updateAttendanceRecord = async (email, status, punchInTime = null, punchOutTime = null, reason = null) => {
    try {
      console.log('🔍 Client: Updating attendance record:', { email, status, punchInTime, punchOutTime, reason });
      
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const updateData = {
        email,
        date: selectedDate,
        status,
        punchInTime,
        punchOutTime,
        reason
      };
      
      console.log('🔍 Client: Sending request to:', `${API_URL}/api/attendance/update`);
      console.log('🔍 Client: Request data:', updateData);
      
      const response = await axios.put(`${API_URL}/api/attendance/update`, updateData, { headers });
      console.log('✅ Client: Updated attendance record:', response.data);
      
      // Refresh data after update
      loadAttendanceData();
      
      return response.data;
    } catch (error) {
      console.error('❌ Client: Failed to update attendance record:', error);
      console.error('❌ Client: Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message;
      setError(`Failed to update attendance: ${errorMessage}`);
      
      // Show a more user-friendly error message
      alert(`Failed to update attendance for ${email}: ${errorMessage}`);
      
      throw error;
    }
  };



  // Print attendance
  const handlePrint = () => {
    // Debug: Log current data
    console.log('Print function called with:', {
      attendanceDataLength: attendanceData?.length,
      stats: stats,
      selectedDate: selectedDate,
      attendanceData: attendanceData
    });

    // Ensure we have current attendance data
    if (!attendanceData || attendanceData.length === 0) {
      alert('No attendance data available to print. Please refresh the page and try again.');
      return;
    }

    // Create a comprehensive print view
    const printContent = `
      <html>
        <head>
          <title>Daily Attendance Report - ${new Date(selectedDate).toLocaleDateString()}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #007bff;
              padding-bottom: 20px;
            }
            .print-title {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 10px;
            }
            .print-subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 5px;
            }
            .print-date {
              font-size: 14px;
              color: #888;
            }
            .stats-section {
              margin: 20px 0;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 5px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-item {
              text-align: center;
              padding: 10px;
              background: white;
              border-radius: 5px;
              border: 1px solid #ddd;
            }
            .stat-number {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
            }
            .stat-label {
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0;
              font-size: 12px;
            }
            th, td { 
              border: 1px solid #333; 
              padding: 8px; 
              text-align: left;
            }
            th { 
              background-color: #007bff; 
              color: white;
              font-weight: bold; 
              text-align: center;
            }
            .present-row { 
              background-color: #d4edda; 
            }
            .absent-row { 
              background-color: #f8d7da; 
            }
            .status-present {
              color: #28a745;
              font-weight: bold;
            }
            .status-absent {
              color: #dc3545;
              font-weight: bold;
            }
            .role-badge {
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
            }
            .role-admin { background-color: #dc3545; color: white; }
            .role-hr { background-color: #17a2b8; color: white; }
            .role-staff { background-color: #28a745; color: white; }
            .role-manager { background-color: #ffc107; color: black; }
            .print-footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Daily Attendance Report</div>
            <div class="print-subtitle">Staff Attendance Management System</div>
            <div class="print-date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>

          <div class="stats-section">
            <h3 style="margin-top: 0; color: #007bff;">Attendance Summary</h3>
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">${stats.total}</div>
                <div class="stat-label">Total Staff</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #28a745;">${stats.present}</div>
                <div class="stat-label">Present</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #dc3545;">${stats.absent}</div>
                <div class="stat-label">Absent</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #007bff;">${stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%</div>
                <div class="stat-label">Attendance Rate</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Company</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${attendanceData && attendanceData.length > 0 ? 
                attendanceData.map((staff, index) => `
                  <tr class="${staff.isPresent ? 'present-row' : 'absent-row'}">
                    <td>${staff.name}</td>
                    <td>${staff.email}</td>
                    <td>
                      <span class="role-badge role-${staff.role?.toLowerCase().replace(' ', '-')}">
                        ${staff.role}
                      </span>
                    </td>
                    <td>${formatDepartment(staff.department)}</td>
                    <td>${staff.company?.name || 'N/A'}</td>
                    <td class="${staff.isPresent ? 'status-present' : 'status-absent'}">
                      ${staff.isPresent ? '✓ Present' : '✗ Absent'}
                    </td>
                  </tr>
                `).join('') : 
                `<tr><td colspan="6" style="text-align: center; padding: 20px; color: #666;">No staff data available</td></tr>`
              }
            </tbody>
          </table>

          <div class="print-footer">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total Staff:</strong> ${stats.total} | <strong>Present:</strong> ${stats.present} | <strong>Absent:</strong> ${stats.absent}</p>
            <p><strong>Attendance Rate:</strong> ${stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%</p>
            <p style="margin-top: 10px; font-size: 10px; color: #999;">
              This report shows attendance for all staff members from the users collection. 
              Staff who punched in today are marked as PRESENT, others as ABSENT.
            </p>
          </div>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Download as PDF
  const handleDownload = () => {
    // Create a comprehensive PDF view with all staff details
    const downloadContent = `
      <html>
        <head>
          <title>Daily Attendance Report - ${new Date(selectedDate).toLocaleDateString()}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
              color: #333;
            }
            .report-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #007bff;
              padding-bottom: 20px;
            }
            .report-title {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 10px;
            }
            .report-subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 5px;
            }
            .report-date {
              font-size: 14px;
              color: #888;
            }
            .summary-section {
              margin: 20px 0;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 5px;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .summary-item {
              text-align: center;
              padding: 10px;
              background: white;
              border-radius: 5px;
              border: 1px solid #ddd;
            }
            .summary-number {
              font-size: 20px;
              font-weight: bold;
              color: #007bff;
            }
            .summary-label {
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0;
              font-size: 11px;
            }
            th, td { 
              border: 1px solid #333; 
              padding: 6px; 
              text-align: left;
            }
            th { 
              background-color: #007bff; 
              color: white;
              font-weight: bold; 
              text-align: center;
            }
            .present-row { 
              background-color: #d4edda; 
            }
            .absent-row { 
              background-color: #f8d7da; 
            }
            .status-present {
              color: #28a745;
              font-weight: bold;
            }
            .status-absent {
              color: #dc3545;
              font-weight: bold;
            }
            .role-badge {
              padding: 2px 4px;
              border-radius: 3px;
              font-size: 9px;
              font-weight: bold;
            }
            .role-admin { background-color: #dc3545; color: white; }
            .role-hr { background-color: #17a2b8; color: white; }
            .role-staff { background-color: #28a745; color: white; }
            .role-manager { background-color: #ffc107; color: black; }
            .report-footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              font-size: 11px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <div class="report-title">Daily Attendance Report</div>
            <div class="report-subtitle">Staff Attendance Management System</div>
            <div class="report-date">Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>

          <div class="summary-section">
            <h3 style="margin-top: 0; color: #007bff;">Attendance Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-number">${stats.total}</div>
                <div class="summary-label">Total Staff</div>
              </div>
              <div class="summary-item">
                <div class="summary-number" style="color: #28a745;">${stats.present}</div>
                <div class="summary-label">Present</div>
              </div>
              <div class="summary-item">
                <div class="summary-number" style="color: #dc3545;">${stats.absent}</div>
                <div class="summary-label">Absent</div>
              </div>
              <div class="summary-item">
                <div class="summary-number" style="color: #007bff;">${stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%</div>
                <div class="summary-label">Attendance Rate</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Company</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${attendanceData && attendanceData.length > 0 ? 
                attendanceData.map((staff, index) => `
                  <tr class="${staff.isPresent ? 'present-row' : 'absent-row'}">
                    <td>${staff.name}</td>
                    <td>${staff.email}</td>
                    <td>
                      <span class="role-badge role-${staff.role?.toLowerCase().replace(' ', '-')}">
                        ${staff.role}
                      </span>
                    </td>
                    <td>${formatDepartment(staff.department)}</td>
                    <td>${staff.company?.name || 'N/A'}</td>
                    <td class="${staff.isPresent ? 'status-present' : 'status-absent'}">
                      ${staff.isPresent ? '✓ Present' : '✗ Absent'}
                    </td>
                  </tr>
                `).join('') : 
                `<tr><td colspan="6" style="text-align: center; padding: 20px; color: #666;">No staff data available</td></tr>`
              }
            </tbody>
          </table>

          <div class="report-footer">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total Staff:</strong> ${stats.total} | <strong>Present:</strong> ${stats.present} | <strong>Absent:</strong> ${stats.absent}</p>
            <p><strong>Attendance Rate:</strong> ${stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%</p>
            <p style="margin-top: 10px; font-size: 10px; color: #999;">
              This report shows attendance for all staff members from the users collection. 
              Staff who punched in today are marked as PRESENT, others as ABSENT.
            </p>
          </div>
        </body>
      </html>
    `;

    // Open download window
    const downloadWindow = window.open('', '_blank');
    downloadWindow.document.write(downloadContent);
    downloadWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      downloadWindow.print();
      downloadWindow.close();
    }, 500);
  };

  if (!currentUser) {
    return <div className="daily-attendance-page">Loading...</div>;
  }

  return (
    <div className="daily-attendance-page">
      <div className="page-header">
        <h2><FaUsers /> Daily Attendance Management</h2>
        <div className="header-actions">
          <button className="refresh-btn" onClick={handleRefresh} disabled={loading}>
            <FaRedo /> Refresh
          </button>
          <button className="print-btn" onClick={handlePrint}>
            <FaPrint /> Print
          </button>
          <button className="download-btn" onClick={handleDownload}>
            <FaDownload /> Download
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Date Selector */}
      <div className="date-selector">
        <label htmlFor="attendance-date">
          <FaCalendar /> Select Date:
        </label>
          <input
            type="date"
          id="attendance-date"
            value={selectedDate}
          onChange={handleDateChange}
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

      {/* Statistics */}
      <div className="attendance-stats">
        <div className="stat-card total">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Staff</div>
          </div>
        </div>
        <div className="stat-card present">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.present}</div>
            <div className="stat-label">Present</div>
        </div>
      </div>
        <div className="stat-card absent">
          <div className="stat-icon">
            <FaTimesCircle />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.absent}</div>
            <div className="stat-label">Absent</div>
          </div>
        </div>
        <div className="stat-card percentage">
          <div className="stat-icon">
            <FaClock />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%
            </div>
            <div className="stat-label">Attendance Rate</div>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="attendance-table-container">
        <div className="table-header">
          <h3>Daily Attendance - {new Date(selectedDate).toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</h3>
        </div>

        {loading ? (
          <div className="loading-message">Loading attendance data...</div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="table-container">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Staff Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData && attendanceData.length > 0 ? (
                    attendanceData.map((staff, index) => (
                      <tr key={staff._id || `staff-${index}`} className={staff.isPresent ? 'present-row' : 'absent-row'}>
                        <td>
                          {staff.name}
                          {staff.isFirebase && (
                            <span style={{ marginLeft: '5px', color: '#4caf50' }} title="Firebase Data">🔥</span>
                          )}
                          {staff.isFromPunching && (
                            <span style={{ marginLeft: '5px', color: '#007bff' }} title="From Punching Records">📱</span>
                          )}
                          {staff.isFromAuth && (
                            <span style={{ marginLeft: '5px', color: '#ff9800' }} title="From Firebase Auth">👤</span>
                          )}
                        </td>
                        <td>{staff.email}</td>
                        <td>
                          <span className={`role-badge role-${staff.role?.toLowerCase().replace(' ', '-')}`}>
                            {staff.role}
                          </span>
                        </td>
                        <td>{formatDepartment(staff.department)}</td>
                        <td>{staff.company?.name || 'N/A'}</td>
                        <td>
                          <span 
                            className="status-badge" 
                            style={{ color: getStatusColor(staff.isPresent) }}
                          >
                            {getStatusIcon(staff.isPresent)}
                            {staff.isPresent ? 'Present' : 'Absent'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            {!staff.isPresent && (
                              <button 
                                className="btn btn-success btn-sm"
                                onClick={() => updateAttendanceRecord(staff.email, 'present', new Date().toISOString())}
                                title="Mark as Present"
                              >
                                ✓
                              </button>
                            )}
                            {staff.isPresent && (
                              <button 
                                className="btn btn-warning btn-sm"
                                onClick={() => updateAttendanceRecord(staff.email, 'absent', null, null, 'Manual override')}
                                title="Mark as Absent"
                              >
                                ✗
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">
                        {stats.total === 0 ? 
                          'No staff data available. Please ensure staff members are registered and have punching records.' : 
                          `No attendance data for ${new Date(selectedDate).toLocaleDateString()}. Total Staff: ${stats.total}, Present: ${stats.present}, Absent: ${stats.absent}`
                        }
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout */}
            <div className="mobile-cards-container">
              {attendanceData && attendanceData.length > 0 ? (
                attendanceData.map((staff, index) => (
                  <div key={staff._id || `staff-${index}`} className={`mobile-card ${staff.isPresent ? 'present-card' : 'absent-card'}`}>
                    <div className="card-header">
                      <div className="card-title">
                        <h4>{staff.name}</h4>
                        <span className={`card-status ${staff.isPresent ? 'status-present' : 'status-absent'}`}>
                          {getStatusIcon(staff.isPresent)}
                          {staff.isPresent ? 'Present' : 'Absent'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-content">
                      <div className="card-row">
                        <span className="card-label">Email:</span>
                        <span className="card-value">{staff.email}</span>
                      </div>
                      
                      <div className="card-row">
                        <span className="card-label">Role:</span>
                        <span className="card-value">
                          <span className={`role-badge role-${staff.role?.toLowerCase().replace(' ', '-')}`}>
                            {staff.role}
                          </span>
                        </span>
                      </div>
                      
                      <div className="card-row">
                        <span className="card-label">Department:</span>
                        <span className="card-value">{formatDepartment(staff.department)}</span>
                      </div>
                      
                      <div className="card-row">
                        <span className="card-label">Company:</span>
                        <span className="card-value">{staff.company?.name || 'N/A'}</span>
                      </div>
                    </div>
                    
                    <div className="card-actions">
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {!staff.isPresent && (
                          <button 
                            className="card-action-btn present-btn"
                            onClick={() => updateAttendanceRecord(staff.email, 'present', new Date().toISOString())}
                            title="Mark as Present"
                          >
                            <FaCheckCircle />
                            Mark Present
                          </button>
                        )}
                        {staff.isPresent && (
                          <button 
                            className="card-action-btn absent-btn"
                            onClick={() => updateAttendanceRecord(staff.email, 'absent', null, null, 'Manual override')}
                            title="Mark as Absent"
                          >
                            <FaTimesCircle />
                            Mark Absent
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="mobile-empty-state">
                  <div className="empty-icon">
                    <FaUsers />
                  </div>
                  <h3>No Attendance Data</h3>
                  <p>
                    {stats.total === 0 ? 
                      'No staff data available. Please ensure staff members are registered and have punching records.' : 
                      `No attendance data for ${new Date(selectedDate).toLocaleDateString()}. Total Staff: ${stats.total}, Present: ${stats.present}, Absent: ${stats.absent}`
                    }
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Summary */}
      <div className="attendance-summary">
        <h4>Summary</h4>
        <p>
          On <strong>{new Date(selectedDate).toLocaleDateString()}</strong>, 
          <strong> {stats.present}</strong> out of <strong>{stats.total}</strong> staff members from the users collection were present.
          Attendance rate: <strong>{stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%</strong>
        </p>
        {stats.absent > 0 && (
          <p className="absent-note">
            <strong>{stats.absent}</strong> staff members were absent today.
          </p>
        )}
      </div>
    </div>
  );
};

export default DailyAttendancePage;